import { useCallback, useMemo } from 'react'
import { Card, CardBody, Col, Row, Button } from 'reactstrap'

import { InputFormat, CustomButton } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'
import { Rules } from 'components/common/utils'

type FormInputProps = {
  items: string[] | null
  maxItems: number
  onChange: (val: (latest: string[] | null) => string[] | null) => void
  titleText?: string
  addButtonText: string
  labelText: string
  onValidate?: (isValid: boolean) => void
}

export const FormInput = ({
  items,
  maxItems,
  onChange,
  titleText,
  addButtonText,
  labelText,
  onValidate,
}: FormInputProps) => {
  const currentItems = useMemo(() => items || [], [items])

  const handleAddItem = useCallback(() => {
    onChange(prev => {
      const updated = [...(prev || []), '']
      return updated.length ? updated : null
    })
  }, [onChange])

  const handleUpdateItem = useCallback(
    (index: number, value: string) => {
      onChange(prev => {
        const updated = (prev || []).map((item, idx) => (idx === index ? value : item))
        return updated.length ? updated : null
      })
    },
    [onChange]
  )

  const handleDeleteItem = useCallback(
    (index: number) => {
      onChange(prev => {
        const updated = (prev || []).filter((_, idx) => idx !== index)
        return updated.length ? updated : null
      })
    },
    [onChange]
  )

  return (
    <>
      {currentItems.length ? (
        <>
          {titleText && <div className="pt-2">{titleText}</div>}
          <div className="d-flex justify-content-end align-self-center mb-2 text-gray">
            {currentItems.length} / {maxItems} 利用中
          </div>
          <Card>
            {currentItems.map((item, index) => (
              <div key={index}>
                <CardBody>
                  <Row>
                    <Col xxl="9">
                      <InputFormat
                        label={labelText}
                        labelSize={COLUMN_SIZES.X_SHORT}
                        className="my-3"
                        value={item}
                        onChange={value => handleUpdateItem(index, value)}
                        validations={[Rules.Required, Rules.IPAddress]}
                        onValidate={isValid => onValidate?.(isValid)}
                      />
                    </Col>
                    <Col xxl="3" className="d-flex justify-content-end align-items-center">
                      {index < maxItems - 1 && index === currentItems.length - 1 && (
                        <CustomButton icon="plus" outline className="my-3 d-block text-nowrap" onClick={handleAddItem}>
                          追加
                        </CustomButton>
                      )}
                      <Button
                        outline
                        color="danger"
                        className="my-3 ms-2 d-block text-nowrap"
                        onClick={() => handleDeleteItem(index)}
                      >
                        削除
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
                {index < currentItems.length - 1 && <hr className="m-0" />}
              </div>
            ))}
          </Card>
        </>
      ) : (
        <CustomButton icon="plus" outline size="sm" onClick={handleAddItem}>
          {addButtonText}
        </CustomButton>
      )}
    </>
  )
}
